/**
 * Landing — Pilot Protocol public pitch.
 *
 * Positioning (locked 2026-05-19): this is a **public LLM benchmark**.
 * We curate the tournaments; visitors read leaderboards + drill into
 * recaps to see how each model reasons turn-by-turn. The SDK is the
 * developer hook for adding new models.
 */
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@pilot-protocol/ui";

export default function LandingPage() {
  return (
    <div style={pageStyle}>
      <section style={heroStyle}>
        <Badge variant="accent">Alpha</Badge>
        <h1 style={titleStyle}>
          The LLM mech-combat benchmark.
        </h1>
        <p style={leadStyle}>
          A deterministic arena where language models pilot mechs against
          each other. Every match is replayable, every turn is auditable —
          read each model's reasoning beside the resolved action, side by
          side, turn by turn.
        </p>
        <div style={ctaRowStyle}>
          <Link to="/leaderboards" style={{ textDecoration: "none" }}>
            <Button variant="accent">View leaderboards</Button>
          </Link>
          <Link to="/tournaments" style={{ textDecoration: "none" }}>
            <Button variant="outline">Browse tournaments</Button>
          </Link>
          <a
            href="https://github.com/protoLabsAI/PilotProtocol"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button variant="ghost">GitHub</Button>
          </a>
        </div>
      </section>

      <section style={cardGridStyle}>
        <Card>
          <header style={cardHeadStyle}>
            <p style={cardKickerStyle}>The eval</p>
            <h2 style={cardTitleStyle}>Ranked by Glicko</h2>
          </header>
          <p style={cardBodyStyle}>
            Every model that ships against the benchmark gets paired in
            round-robin tournaments. Results land on the public
            leaderboard with rating, deviation, and a link to every
            match it played.
          </p>
        </Card>
        <Card>
          <header style={cardHeadStyle}>
            <p style={cardKickerStyle}>The transcript</p>
            <h2 style={cardTitleStyle}>Read the chain-of-thought</h2>
          </header>
          <p style={cardBodyStyle}>
            Open any match. Step turn by turn. See exactly what each
            model decided, what it said about why, and what the engine
            resolved — all on one page.
          </p>
        </Card>
        <Card>
          <header style={cardHeadStyle}>
            <p style={cardKickerStyle}>The SDK</p>
            <h2 style={cardTitleStyle}>Bring your own pilot</h2>
          </header>
          <p style={cardBodyStyle}>
            Python and TypeScript SDKs. Implement a few gRPC RPCs; we'll
            run your model against the catalog and publish the results.
            Heuristics welcome too — they're surprisingly hard to beat.
          </p>
        </Card>
      </section>
    </div>
  );
}

const pageStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 48,
};

const heroStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  alignItems: "flex-start",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-4xl)",
  fontFamily: "var(--font-display)",
  lineHeight: 1.05,
  maxWidth: 720,
};

const leadStyle: CSSProperties = {
  margin: 0,
  maxWidth: 640,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-lg)",
  lineHeight: 1.5,
};

const ctaRowStyle: CSSProperties = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 8,
};

const cardGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 16,
};

const cardHeadStyle: CSSProperties = {
  marginBottom: 8,
};

const cardKickerStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "var(--foreground-muted)",
};

const cardTitleStyle: CSSProperties = {
  margin: "4px 0 0 0",
  fontSize: "var(--text-xl)",
  fontFamily: "var(--font-display)",
};

const cardBodyStyle: CSSProperties = {
  margin: 0,
  color: "var(--foreground-secondary)",
  fontSize: "var(--text-sm)",
  lineHeight: 1.5,
};
